export default defineAppConfig({
   ui: {
      colors: {
         primary: 'villa-navy',
         secondary: 'villa-blue',
         success: 'villa-green',
         info: 'villa-grey',
         warning: 'villa-amber',
         error: 'villa-rose',
         neutral: 'villa-teal',
      },
      button: {
         slots: {
            base: 'rounded-small font-medium inline-flex items-center cursor-pointer select-none transition-all duration-200 hover:brightness-90 active:scale-[0.99] active:brightness-85 disabled:cursor-not-allowed disabled:opacity-75',
         },
         variants: {
            size: {
               md: {
                  base: 'px-4 h-10 text-sm gap-2',
                  leadingIcon: 'size-5',
                  trailingIcon: 'size-5',
               },
               lg: {
                  base: 'px-4 h-input-large text-base gap-2',
                  leadingIcon: 'size-5',
                  trailingIcon: 'size-5',
               },
            },
         },
         compoundVariants: [
            { size: 'md' as const, square: true, class: 'p-2.5 h-auto' },
            { size: 'lg' as const, square: true, class: 'p-2.5 h-auto' },
         ],
         defaultVariants: {
            size: 'md' as const,
            color: 'primary' as const,
            variant: 'solid' as const,
         },
      },
      input: {
         slots: {
            base: 'rounded-small bg-light-grey placeholder-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
         },
         variants: {
            size: {
               md: {
                  base: 'h-10 px-4 text-sm',
               },
            },
         },
         defaultVariants: {
            size: 'md' as const,
            variant: 'none' as const,
         },
      },
      textarea: {
         slots: {
            base: 'rounded-small bg-light-grey placeholder-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors resize-none',
         },
         defaultVariants: {
            variant: 'none' as const,
         },
      },
      select: {
         slots: {
            base: 'rounded-small bg-light-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
         },
         variants: {
            size: {
               md: {
                  base: 'h-10 text-sm',
               },
            },
         },
         defaultVariants: {
            variant: 'none' as const,
            size: 'md' as const,
         },
      },
      selectMenu: {
         slots: {
            base: 'rounded-small bg-light-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
            content: 'rounded-small border border-light-grey shadow-[0px_5px_10px_0px_rgba(0,0,0,0.10)]',
            item: 'rounded-xsmall',
         },
         variants: {
            size: {
               md: {
                  base: 'h-10 text-sm',
               },
            },
         },
         defaultVariants: {
            variant: 'none' as const,
            size: 'md' as const,
         },
      },
      inputMenu: {
         slots: {
            root: 'rounded-small text-black outline-0 border border-light-grey focus-within:border-grey transition-colors h-10',
            base: 'rounded-small bg-light-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
            content: 'rounded-small border border-light-grey shadow-[0px_5px_10px_0px_rgba(0,0,0,0.10)]',
            item: 'rounded-xsmall',
         },
         defaultVariants: {
            variant: 'none' as const,
            size: 'md' as const,
         },
      },
      checkbox: {
         slots: {
            base: 'rounded border-grey',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      radioGroup: {
         slots: {
            indicator: 'border-grey',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      switch: {
         slots: {
            base: 'data-[state=unchecked]:bg-light-blue',
            thumb: 'bg-white',
         },
         defaultVariants: {
            color: 'primary' as const,
            size: 'lg' as const,
         },
      },
      card: {
         slots: {
            root: 'rounded-base overflow-hidden bg-white',
         },
         defaultVariants: {
            variant: 'outline' as const,
         },
      },
      modal: {
         slots: {
            content: 'rounded-base',
         },
      },
      slideover: {
         slots: {
            content: 'rounded-base',
         },
      },
      drawer: {
         slots: {
            content: 'rounded-base',
         },
      },
      tooltip: {
         slots: {
            content:
               'rounded-small bg-white text-black border border-light-grey shadow-[0px_5px_10px_0px_rgba(0,0,0,0.10)]',
         },
      },
      popover: {
         slots: {
            content:
               'rounded-small bg-white text-black border border-light-grey shadow-[0px_5px_10px_0px_rgba(0,0,0,0.10)]',
         },
      },
      accordion: {
         slots: {
            item: 'border-b border-grey/20 last:border-b-0',
            trigger: 'font-title font-bold text-base py-4',
            trailingIcon: 'text-grey',
            body: 'text-sm pb-4',
         },
      },
      tabs: {
         slots: {
            list: 'bg-transparent rounded-none gap-2 p-0',
            indicator: 'hidden',
         },
         variants: {
            variant: {
               pill: {
                  trigger:
                     'rounded-small border border-primary text-primary bg-white data-[state=active]:bg-primary data-[state=active]:text-white font-medium',
               },
               link: {
                  trigger:
                     'font-medium text-grey data-[state=active]:text-primary border-b-2 border-transparent data-[state=active]:border-primary rounded-none',
               },
            },
         },
         defaultVariants: {
            color: 'primary' as const,
            variant: 'pill' as const,
            size: 'md' as const,
         },
      },
      alert: {
         slots: {
            root: 'rounded-base p-4',
            title: 'font-title font-bold',
            description: 'text-sm',
         },
         variants: {
            variant: {
               outline: {
                  root: 'border-2 bg-transparent',
               },
               soft: {
                  root: 'border-2',
               },
               subtle: {
                  root: 'border-2',
               },
            },
         },
         defaultVariants: {
            variant: 'soft' as const,
         },
      },
      badge: {
         slots: {
            base: 'rounded-small font-medium',
         },
         defaultVariants: {
            color: 'primary' as const,
            variant: 'soft' as const,
            size: 'md' as const,
         },
      },
      avatar: {
         slots: {
            root: 'rounded-full bg-light-grey text-primary font-title font-bold ring-1 ring-[var(--ui-border)]',
         },
      },
      chip: {
         defaultVariants: {
            color: 'success' as const,
         },
      },
      progress: {
         slots: {
            root: 'rounded-full',
            indicator: 'rounded-full',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      toast: {
         slots: {
            root: 'rounded-base border-2 bg-white p-4 __shadow',
            title: 'font-title font-bold',
            description: 'text-sm',
         },
      },
      banner: {
         slots: {
            root: 'font-medium',
            title: 'text-sm',
         },
      },
      skeleton: {
         slots: {
            base: 'bg-light-grey rounded-base',
         },
      },
      breadcrumb: {
         slots: {
            link: 'text-grey hover:text-primary transition-colors',
            linkActive: 'text-black font-medium',
         },
      },
      pagination: {
         slots: {
            root: 'gap-1',
            item: 'rounded-small',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      stepper: {
         slots: {
            title: 'font-title font-bold text-sm',
            description: 'text-xs text-grey',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      navigationMenu: {
         slots: {
            link: 'rounded-small',
         },
      },
      table: {
         slots: {
            th: 'font-title font-bold text-xs uppercase tracking-wider text-grey',
            td: 'text-sm',
         },
      },
      formField: {
         slots: {
            label: 'font-title font-bold text-sm text-black',
            description: 'text-xs text-grey',
            help: 'text-xs text-grey',
            error: 'text-xs',
         },
      },
      listbox: {
         slots: {
            content: 'rounded-small border border-light-grey',
            item: 'rounded-xsmall',
         },
      },
      commandPalette: {
         slots: {
            input: 'bg-transparent border-b border-light-grey',
            item: 'rounded-xsmall',
         },
      },
      dropdownMenu: {
         slots: {
            content: 'rounded-small border border-light-grey shadow-[0px_5px_10px_0px_rgba(0,0,0,0.10)]',
            item: 'rounded-xsmall !transition-none',
            itemLeadingIcon: '!transition-none',
            itemTrailingIcon: '!transition-none',
            label: '!transition-none',
         },
      },
      contextMenu: {
         slots: {
            content: 'rounded-small border border-light-grey shadow-[0px_5px_10px_0px_rgba(0,0,0,0.10)]',
            item: 'rounded-xsmall !transition-none',
            itemLeadingIcon: '!transition-none',
            itemTrailingIcon: '!transition-none',
            label: '!transition-none',
         },
      },
      separator: {
         slots: {
            root: 'bg-grey/20',
         },
      },
      inputNumber: {
         slots: {
            base: 'rounded-small bg-light-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
         },
         defaultVariants: {
            size: 'md' as const,
            variant: 'none' as const,
         },
      },
      inputTags: {
         slots: {
            root: 'rounded-small bg-light-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
            item: 'rounded-xsmall',
         },
         defaultVariants: {
            size: 'md' as const,
            variant: 'none' as const,
         },
      },
      pinInput: {
         slots: {
            base: 'rounded-small bg-light-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
         },
         defaultVariants: {
            size: 'md' as const,
            variant: 'none' as const,
         },
      },
      inputDate: {
         slots: {
            base: 'rounded-small bg-light-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
         },
         defaultVariants: {
            size: 'md' as const,
            variant: 'none' as const,
         },
      },
      inputTime: {
         slots: {
            base: 'rounded-small bg-light-grey text-black outline-0 border border-light-grey focus-within:border-grey transition-colors',
         },
         defaultVariants: {
            size: 'md' as const,
            variant: 'none' as const,
         },
      },
      colorPicker: {
         slots: {
            root: 'rounded-small',
         },
      },
      slider: {
         slots: {
            root: 'rounded-full',
            range: 'rounded-full',
            thumb: 'rounded-full',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      fileUpload: {
         slots: {
            root: 'rounded-base',
            base: 'rounded-base border-2 border-dashed border-light-grey bg-light-grey/50',
            icon: 'text-grey',
            label: 'font-title font-bold text-sm',
            description: 'text-xs text-grey',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      calendar: {
         slots: {
            root: 'rounded-base',
            headCell: 'text-grey text-xs',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      timeline: {
         slots: {
            title: 'font-title font-bold text-sm',
            description: 'text-xs text-grey',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      tree: {
         slots: {
            link: 'rounded-xsmall',
            linkLabel: 'text-sm',
         },
         defaultVariants: {
            color: 'primary' as const,
         },
      },
      carousel: {
         slots: {
            dot: 'rounded-full',
         },
      },
      empty: {
         slots: {
            root: 'rounded-base',
            title: 'font-title font-bold text-sm',
            description: 'text-xs text-grey',
         },
      },
      user: {
         slots: {
            name: 'font-title font-bold text-sm',
            description: 'text-xs text-grey',
         },
      },
      kbd: {
         defaultVariants: {
            variant: 'outline' as const,
            size: 'md' as const,
         },
      },
      collapsible: {
         slots: {
            content: 'rounded-small',
         },
      },
      fieldGroup: {
         variants: {
            orientation: {
               horizontal: 'inline-flex -space-x-px items-center',
            },
         },
         slots: {
            base: 'rounded-small',
         },
      },
   },
})
